/**
 * Клиентская телеметрия admin-SPA (docs/service/37 §6).
 *
 * Собирает продуктовые события (page_view / клики / js-ошибки / api-ошибки) и
 * батчами шлёт на POST /api/v1/telemetry/events. ПРИНЦИП: телеметрия НИКОГДА не
 * ломает UX — весь модуль в try/catch, сбой сети/500 → тихий дроп без ретраев.
 *
 * ВНИМАНИЕ: держать в синхроне с apps/miniapp/src/lib/track.ts (осознанный
 * дубль — у SPA разные api-клиенты и сборки; см. док §7).
 */

const BASE_URL: string = import.meta.env.VITE_API_URL ?? '/api/v1';
const ENDPOINT = `${BASE_URL}/telemetry/events`;

const FLUSH_INTERVAL_MS = 10_000;
const FLUSH_AT_COUNT = 20;
/** Кап js-ошибок на сессию — цикл ошибок не должен залить БД. */
const MAX_JS_ERRORS = 10;

interface QueuedEvent {
  type: string;
  route?: string;
  label?: string;
  props?: Record<string, unknown>;
  at: string;
}

let queue: QueuedEvent[] = [];
let sessionId = '';
let timer: ReturnType<typeof setInterval> | null = null;
let started = false;
const seenErrors = new Set<string>();

/** UUID сессии-вкладки (живёт вкладку). Фолбэк — псевдо-uuid. */
function getSessionId(): string {
  if (sessionId) return sessionId;
  try {
    const key = 'tel_sid';
    const existing = sessionStorage.getItem(key);
    if (existing) {
      sessionId = existing;
      return sessionId;
    }
    sessionId = uuid();
    sessionStorage.setItem(key, sessionId);
  } catch {
    sessionId = uuid();
  }
  return sessionId;
}

function uuid(): string {
  try {
    if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  } catch {
    /* ignore */
  }
  // Псевдо-uuid v4-подобный (только для сессии, не для безопасности).
  return '10000000-1000-4000-8000-100000000000'.replace(/[018]/g, (ch) => {
    const c = Number(ch);
    return (c ^ (Math.floor(Math.random() * 16) & (15 >> (c / 4)))).toString(16);
  });
}

function nowIso(): string {
  try {
    return new Date().toISOString();
  } catch {
    return '1970-01-01T00:00:00.000Z';
  }
}

/** Отправить накопленное. useBeacon=true — через sendBeacon (уход со страницы). */
function flush(useBeacon = false): void {
  try {
    if (queue.length === 0) return;
    const batch = { session_id: getSessionId(), events: queue };
    queue = [];
    const blob = new Blob([JSON.stringify(batch)], { type: 'application/json' });

    if (useBeacon && typeof navigator !== 'undefined' && 'sendBeacon' in navigator) {
      navigator.sendBeacon(ENDPOINT, blob);
      return;
    }
    void fetch(ENDPOINT, {
      method: 'POST',
      body: blob,
      credentials: 'include',
      keepalive: true,
    }).catch(() => {
      /* дроп — телеметрия не ретраит */
    });
  } catch {
    /* никогда не бросаем из телеметрии */
  }
}

/** Положить событие в очередь; флаш по достижении порога. */
export function track(
  type: string,
  data?: { route?: string; label?: string; props?: Record<string, unknown> },
): void {
  try {
    queue.push({
      type,
      ...(data?.route ? { route: data.route } : {}),
      ...(data?.label ? { label: data.label } : {}),
      ...(data?.props ? { props: data.props } : {}),
      at: nowIso(),
    });
    if (queue.length >= FLUSH_AT_COUNT) flush();
  } catch {
    /* ignore */
  }
}

/** Просмотр экрана (нормализованный роут-паттерн передаёт вызывающий). */
export function trackPageView(route: string): void {
  track('page_view', { route });
}

/**
 * Сбой обновления service worker — не ошибка кода, а сетевая осечка.
 *
 * Разбор телеметрии 28.08.2026: из 99 записей `ui.js_error` за две недели 68
 * оказались этими двумя сообщениями. Открытая вкладка спрашивает сервер о новой
 * версии раз в минуту и при каждом возврате в приложение; любая осечка сети даёт
 * запись, а следующая попытка через минуту проходит. Чинить нечего — но в этом
 * шуме потонула настоящая поломка (свободная переменная `isMobile`, 8 записей),
 * и её никто не заметил.
 *
 * Поэтому такие сообщения уходят СВОИМ типом события: здоровье SW по-прежнему
 * видно в «Логах», а список ошибок кода перестаёт быть на две трети мусором.
 */
export function errorEventType(message: string): 'ui.sw_error' | 'ui.js_error' {
  const swNoise =
    message.includes('Failed to update a ServiceWorker') ||
    (message.includes('/sw.js') && message.includes('load failed'));
  return swNoise ? 'ui.sw_error' : 'ui.js_error';
}

/** JS-ошибка/reject — дедуп по (сообщение+первый кадр), кап на сессию. */
export function trackError(message: string, topFrame?: string, route?: string): void {
  try {
    const key = `${message}::${topFrame ?? ''}`;
    if (seenErrors.has(key)) return;
    if (seenErrors.size >= MAX_JS_ERRORS) return;
    seenErrors.add(key);
    track(errorEventType(message), {
      ...(route ? { route } : {}),
      props: { message: message.slice(0, 300), top_frame: (topFrame ?? '').slice(0, 200) },
    });
  } catch {
    /* ignore */
  }
}

/**
 * Нормализованный путь запроса для группировки в «Логах»: убираем query,
 * маскируем uuid и числовые id в `:id` (Ф7 плана 38 — раньше ui.api_error шёл
 * без пути, паттерны сливались). Маскирование ограничивает кардинальность и не
 * пускает возможный PII из id-сегментов.
 */
export function maskPath(rawPath: string | undefined): string | undefined {
  if (!rawPath) return undefined;
  const noQuery = rawPath.split('?')[0] ?? rawPath;
  return noQuery
    .replace(/\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi, '/:id')
    .replace(/\/\d+(?=\/|$)/g, '/:id');
}

/**
 * Ошибка API глазами пользователя (§6.2) — код+статус+нормализованный путь,
 * без тела запроса. `route` — экран пользователя (location.pathname).
 */
export function trackApiError(
  code: string | undefined,
  status: number,
  opts?: { path?: string; method?: string; route?: string },
): void {
  const path = maskPath(opts?.path);
  const route = opts?.route ?? routeHint();
  track('ui.api_error', {
    ...(route ? { route } : {}),
    props: {
      code: code ?? `http_${status}`,
      status,
      ...(path ? { path } : {}),
      ...(opts?.method ? { method: opts.method } : {}),
    },
  });
}

/** Найти короткий текст статичной UI-надписи у цели клика (фильтр PII §6.2). */
function labelFor(el: Element): string | null {
  const aria = el.getAttribute('aria-label');
  if (aria && aria.length <= 40) return aria.trim();
  const dt = el.getAttribute('data-track');
  if (dt) return dt.slice(0, 40);
  const tag = el.tagName.toLowerCase();
  if (tag === 'button' || tag === 'a') {
    const text = (el.textContent ?? '').trim();
    // Только короткие статичные подписи («Сохранить»); строки с именами/данными
    // клиента длинные и не проходят фильтр.
    if (text && text.length <= 40) return text;
  }
  return null;
}

/**
 * Запустить сбор: глобальный клик-listener, js-ошибки, флаш по таймеру и на
 * уход со страницы. Идемпотентно (guard started). Вызывается один раз из main.
 */
export function initTelemetry(): void {
  if (started || typeof window === 'undefined') return;
  started = true;
  try {
    document.addEventListener(
      'click',
      (e) => {
        try {
          const target = e.target as Element | null;
          const el = target?.closest?.('[data-track], button, a, [role="button"]');
          if (!el) return;
          const label = labelFor(el);
          if (!label) return;
          const route = routeHint();
          track('ui.click', { ...(route ? { route } : {}), label });
        } catch {
          /* ignore */
        }
      },
      { capture: true },
    );

    window.addEventListener('error', (e) => {
      trackError(String(e.message ?? 'error'), firstFrame(e.error), routeHint());
    });
    window.addEventListener('unhandledrejection', (e) => {
      const reason = (e as PromiseRejectionEvent).reason;
      trackError(
        String((reason as { message?: string })?.message ?? reason ?? 'unhandledrejection'),
        firstFrame(reason),
        routeHint(),
      );
    });

    timer = setInterval(() => flush(), FLUSH_INTERVAL_MS);
    timer.unref?.();
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') flush(true);
    });
    window.addEventListener('pagehide', () => flush(true));
  } catch {
    /* ignore */
  }
}

/** Грубый роут из адресной строки (для клик/ошибок; page_view нормализуется отдельно). */
function routeHint(): string | undefined {
  try {
    return window.location?.pathname;
  } catch {
    return undefined;
  }
}

/** Первый кадр стека (для дедупа ошибок). */
function firstFrame(err: unknown): string | undefined {
  const stack = (err as { stack?: string } | null)?.stack;
  if (!stack) return undefined;
  const lines = stack.split('\n').map((l) => l.trim());
  return lines.find((l) => l.startsWith('at ')) ?? lines[1];
}
